import { Box, Typography } from "@mui/material";
import type { JSX } from "react";
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

import { Spacing } from "Infrastructure/Themes/Themes";
import { StyledInputLabel } from "Shared/StyledComponents";
import { TipTapEditor } from "Shared/TextEditor/TipTapEditor";

interface RichTextEditorProps<T extends FieldValues> {
  name: FieldPath<T>;
  control: Control<T>;
  errors: FieldErrors<T>;
  label?: string;
  placeholder?: string;
  labelEndAdornment?: JSX.Element;
  onPlainTextChange?: (plainText: string) => void;
}

export const RichTextEditor = <T extends FieldValues>({
  name,
  control,
  errors,
  label,
  placeholder,
  labelEndAdornment,
  onPlainTextChange,
}: RichTextEditorProps<T>) => {
  const error = errors[name];

  return (
    <Box>
      {label && (
        <Box sx={{ display: "flex", alignItems: "center", mb: Spacing.XS }}>
          <StyledInputLabel $hasError={!!error}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "12px",
                fontWeight: 400,
                lineHeight: 1,
              }}
            >
              {label}
            </Typography>
          </StyledInputLabel>
          {labelEndAdornment && <Box ml={0.5}>{labelEndAdornment}</Box>}
        </Box>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field: { value, onChange } }) => (
          <TipTapEditor
            value={value ?? ""}
            onChange={(html: string, plainText: string) => {
              onChange(html);
              onPlainTextChange?.(plainText);
            }}
            placeholder={placeholder}
            hasError={!!error}
          />
        )}
      />
      {error && (
        <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
          {error.message as string}
        </Typography>
      )}
    </Box>
  );
};
